/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { Label } from '@fluentui/react/lib/Label';
import { Link } from '@fluentui/react/lib/Link';
import { IStackItemProps, IStackItemSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { openUrlInNewTab } from 'src/core/utils/routingUtils';
import { AzureSwitchTenantModal } from 'src/modules/azure/components/AzureSwitchTenantModal';
import { useAzureResourcesManagement } from 'src/modules/azure/hooks/useAzureResourcesManagement';

const messages = defineMessages({
    luisTitle: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.luisTitle',
        defaultMessage: 'Welcome to the Language Understanding Intelligent Service (LUIS)!'
    },
    textAnalyticsTitle: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.textAnalyticsTitle',
        defaultMessage: 'Welcome to {brandName}!'
    },
    learnMore: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.learnMore',
        defaultMessage: 'Learn more about creating an Azure resource.'
    },
    subscriptionsLabel: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.subscriptionsLabel',
        defaultMessage: 'Subscription'
    },
    subscriptionsSelectionPlaceholder: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.subscriptionsSelectionPlaceholder',
        defaultMessage: 'Select subscription'
    },
    subscriptionsSearchPlaceholder: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.subscriptionsSearchPlaceholder',
        defaultMessage: 'Search subscription'
    },
    createAzureAccountButtonText: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.createAzureAccountButtonText',
        defaultMessage: 'Create a free Azure account'
    },
    alreadyCreatedAccount: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.alreadyCreatedAccount',
        defaultMessage: 'Already created your account? '
    },
    refreshThePage: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.refreshThePage',
        defaultMessage: 'Refresh the page to update'
    },
    loadingSubscriptions: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.loadingSubscriptions',
        defaultMessage: 'Loading subscriptions...'
    },
    loadingAuthoringResources: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.loadingAuthoringResources',
        defaultMessage: 'Loading authoring resources...'
    },
    selectOrCreateAuthoringResources: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.selectOrCreateAuthoringResources',
        defaultMessage: 'Select or create an authoring resource'
    },
    createAuthoringResources: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.createAuthoringResources',
        defaultMessage: 'Create an authoring resource'
    },
    luisModalTextExistingSubscription: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.luisModalTextExistingSubscription',
        defaultMessage:
            'To access LUIS, you need an authoring resource. You can use an existing resource in your Azure subscription or create a new one.'
    },
    luisModalTextNoSubscription: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.luisModalTextNoSubscription',
        defaultMessage: `To access LUIS, you need an authoring resource as part of an Azure subscription. To use an authoring resource,
create an Azure account to get a subscription. Once you’ve created an Azure subscription simply sign back in or refresh this page.`
    },
    textAnalyticsModalTextExistingSubscription: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.textAnalyticsModalTextExistingSubscription',
        defaultMessage:
            'To access {brandName}, you need an authoring resource. You can use an existing resource in your Azure subscription or create a new one.'
    },
    textAnalyticsModalTextNoSubscription: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.textAnalyticsModalTextNoSubscription',
        defaultMessage: `To access {brandName}, you need an authoring resource as part of an Azure subscription.
To use an authoring resource, create an Azure account to get a subscription. Once you’ve created an Azure subscription simply sign back in or refresh this page.`
    },
    azureDirectory: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.azureDirectory',
        defaultMessage: 'Current Azure directory'
    },
    switchAzureDirectory: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.switchAzureDirectory',
        defaultMessage: 'Switch to a different Azure directory'
    },
    directoryName: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.directoryName',
        defaultMessage: 'Name'
    },
    directoryId: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.directoryId',
        defaultMessage: 'Id'
    },
    logoImageAltText: {
        id: 'startupConfigs.chooseAzureSubscriptionModal.logoImageAltText',
        defaultMessage: 'Logo image.'
    }
});

const logoContainerStyles = classNamesFunction<IStackItemProps, IComponentStyles<IStackItemSlots>>()({
    root: { paddingRight: 32, paddingBottom: 40 }
});

const { styleUlist } = getStylistV2('ChooseAzureSubscriptionModal');
const ShiftedUList = styleUlist('ShiftedUList', { margin: 0, listStyle: 'unset', marginLeft: 20, marginBottom: 5 });

export type ChooseAzureSubscriptionModalProps = Omit<BasicModalProps<object>, 'onSubmit'> & {
    /**
     * Filter function used for filtering azure resources.
     */
    csAccountFilterPredicate: (account: AzureCsAccountStore) => boolean;

    /**
     * Callback function executing upon selecting a subscription.
     */
    onSubmit: (subscriptionId: string, state: 'create' | 'select') => void;
};

const customTextBrandName = 'Custom Text';
const textAnalyticsBrandName = 'Text Analytics';

/**
 * A modal for selecting an azure subscription.
 */
export const ChooseAzureSubscriptionModal = observer((props: ChooseAzureSubscriptionModalProps) => {
    const { onDismiss, csAccountFilterPredicate, onSubmit, ...modalProps } = props;
    const {
        rootStore: {
            azureStore: { subscriptionStoreHouse, tenantStoreHouse, session }
        },
        uiStore: { portal }
    } = useGlobalState();
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState<string>();
    const { resourcesTracker, resources } = useAzureResourcesManagement({
        subscriptionId: selectedSubscriptionId,
        resourceStrategy: csAccountFilterPredicate
    });

    const [isSwitchTenantModalHidden, setIsSwitchTenantModalHidden] = React.useState(true);

    if (portal.kind === 'languageStudio' && portal.subPath === 'root') {
        return null;
    }

    const inLuis = portal.kind === 'luis' || (portal.kind === 'languageStudio' && portal.subPath === 'luis');

    const currentTenant = tenantStoreHouse.findById(session.tenantId);
    const subscriptions = subscriptionStoreHouse.items;
    const userHasAzureAccount = subscriptionStoreHouse.items.length > 0 || subscriptionStoreHouse.tracker.isRunning;
    const dropdownPlaceholder = subscriptionStoreHouse.tracker.isRunning
        ? localize(messages.loadingSubscriptions)
        : localize(messages.subscriptionsSelectionPlaceholder);

    const getButtonText = () => {
        if (resourcesTracker.isRunning) {
            return localize(messages.loadingAuthoringResources);
        }
        if (resources.length !== 0 || !resourcesTracker.isCompleted) {
            return localize(messages.selectOrCreateAuthoringResources);
        }
        return localize(messages.createAuthoringResources);
    };

    const getPortalKind = () => {
        if (portal.kind === 'luis' || (portal.kind === 'languageStudio' && portal.subPath === 'luis')) {
            return 'luis';
        }
        if (portal.kind === 'languageStudio' && portal.subPath === 'customText') {
            return 'customText';
        }
        return 'customtext';
    };

    const getTitle = () => {
        const portalKind = getPortalKind();
        switch (portalKind) {
            case 'luis':
                return localize(messages.luisTitle);
            case 'customtext':
                return localize(messages.textAnalyticsTitle, { brandName: textAnalyticsBrandName });
            case 'customText':
            default:
                return localize(messages.textAnalyticsTitle, { brandName: customTextBrandName });
        }
    };

    const getModalTextNoSubscription = () => {
        const portalKind = getPortalKind();
        switch (portalKind) {
            case 'luis':
                return localize(messages.luisModalTextNoSubscription);
            case 'customtext':
                return localize(messages.textAnalyticsModalTextNoSubscription, { brandName: textAnalyticsBrandName });
            case 'customText':
            default:
                return localize(messages.textAnalyticsModalTextNoSubscription, { brandName: customTextBrandName });
        }
    };

    const getModalTextExistingSubscription = () => {
        const portalKind = getPortalKind();
        switch (portalKind) {
            case 'luis':
                return localize(messages.luisModalTextExistingSubscription);
            case 'customtext':
                return localize(messages.textAnalyticsModalTextExistingSubscription, { brandName: textAnalyticsBrandName });
            case 'customText':
            default:
                return localize(messages.textAnalyticsModalTextExistingSubscription, { brandName: customTextBrandName });
        }
    };

    const imageUrl = inLuis
        ? getAssetsUrl('assets/images/unifiedLandingPage/LUIS.svg')
        : getAssetsUrl('assets/images/unifiedLandingPage/TextAnalytics.svg');

    const handleButtonClick = () => {
        if (selectedSubscriptionId) {
            onSubmit(selectedSubscriptionId, resources.length === 0 ? 'create' : 'select');
        }
    };

    const tenantSelection = () => (
        <Stack>
            <Label>{localize(messages.azureDirectory)}</Label>

            {currentTenant && (
                <ShiftedUList>
                    <li>
                        {localize(messages.directoryName)}: <LuisText>{currentTenant.name}</LuisText>
                    </li>
                    <li>
                        {localize(messages.directoryId)}: <LuisText>{currentTenant.id}</LuisText>
                    </li>
                </ShiftedUList>
            )}

            <Link data-automation-id="tenantModalLink" onClick={() => setIsSwitchTenantModalHidden(false)}>
                {localize(messages.switchAzureDirectory)}
            </Link>
        </Stack>
    );

    const subscriptionSelectionScreen = (
        <>
            <LuisText>
                {getModalTextExistingSubscription()}
                &nbsp;
                <ExternalLink href="https://aka.ms/luisresources">{localize(messages.learnMore)}</ExternalLink>
            </LuisText>

            <Stack tokens={{ childrenGap: 12 }}>
                {tenantSelection()}

                <LuisDropdown
                    placeholder={dropdownPlaceholder}
                    selectedKey={selectedSubscriptionId}
                    data-automation-id="subscriptionsDropdown"
                    label={localize(messages.subscriptionsLabel)}
                    disabled={subscriptionStoreHouse.tracker.isRunning}
                    searchBoxPlaceholder={localize(messages.subscriptionsSearchPlaceholder)}
                    onChange={(_, option) => setSelectedSubscriptionId(option.key as string)}
                    options={subscriptions.map(subscription => ({ key: subscription.id, text: subscription.name }))}
                />

                <PrimaryButton
                    text={getButtonText()}
                    onClick={handleButtonClick}
                    data-automation-id="subscriptionsContinueButton"
                    disabled={!selectedSubscriptionId || resourcesTracker.isRunning}
                />
            </Stack>
        </>
    );
    const azureAccountCreationScreen = (
        <>
            <LuisText styles={{ root: { flex: 1 } }}>
                {getModalTextNoSubscription()}
                &nbsp;
                <ExternalLink href="https://aka.ms/luisresources">{localize(messages.learnMore)}</ExternalLink>
            </LuisText>

            <Stack horizontalAlign="start" tokens={{ childrenGap: 12, padding: '0 0 16px 0' }}>
                {tenantSelection()}

                <PrimaryButton
                    data-automation-id="createAzureAccountButton"
                    text={localize(messages.createAzureAccountButtonText)}
                    onClick={() => openUrlInNewTab('https://azure.microsoft.com/free/cognitive-services/')}
                />

                <LuisText>
                    {localize(messages.alreadyCreatedAccount)}
                    <ExternalLink onClick={() => window.location.reload()}>{localize(messages.refreshThePage)}</ExternalLink>
                </LuisText>
            </Stack>
        </>
    );

    return (
        <BasicModal
            shouldRenderFooter={false}
            minWidth={isLargeOrGreater && 740}
            dialogContentProps={{ title: getTitle() }}
            {...modalProps}>
            <Stack horizontal tokens={{ childrenGap: 56, padding: '36px 0 0 0' }}>
                <Stack tokens={{ childrenGap: 52 }}>{userHasAzureAccount ? subscriptionSelectionScreen : azureAccountCreationScreen}</Stack>

                {isLargeOrGreater && (
                    <StackItem grow styles={logoContainerStyles}>
                        <Image width={240} height={240} src={imageUrl} alt={localize(messages.logoImageAltText)} />
                    </StackItem>
                )}
            </Stack>

            <AzureSwitchTenantModal
                hidden={isSwitchTenantModalHidden}
                onDismiss={() => setIsSwitchTenantModalHidden(true)}
                onSubmit={() => setIsSwitchTenantModalHidden(true)}
            />
        </BasicModal>
    );
});
